import React, { useCallback, useEffect, useRef, useState } from 'react';
import Session from 'supertokens-web-js/recipe/session';
import { IDLE_SESSION_CONFIG } from '@/config/idleSessionConfig';
import { sessionApi } from '@/api/session';
import { IdleDetectionContext, IdleDetectionContextValue } from './IdleDetectionContext';
import { useCrossTabSync } from './useCrossTabSync';

interface IdleDetectionProviderProps {
  children: React.ReactNode;
}

export const IdleDetectionProvider: React.FC<IdleDetectionProviderProps> = ({ children }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(IDLE_SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_MS);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const lastActivityRef = useRef<number>(Date.now());
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const {
    broadcastActivity,
    broadcastLogout,
    broadcastWarning,
    onActivityReceived,
    onLogoutReceived,
    onWarningReceived,
  } = useCrossTabSync();

  /**
   * Records user activity (API request was made).
   * Updates local state and broadcasts to other tabs.
   */
  const recordActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;

    try {
      localStorage.setItem(IDLE_SESSION_CONFIG.STORAGE_KEY, now.toString());
    } catch (e) {}

    broadcastActivity(now);

    if (showWarning) {
      setShowWarning(false);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [showWarning, broadcastActivity]);

  /**
   * Extends the session by calling the backend API.
   */
  const logoutRef = useRef<() => Promise<void>>(() => Promise.resolve());

  const extendSession = useCallback(async () => {
    try {
      await sessionApi.extend();
      recordActivity();
    } catch (error) {
      await logoutRef.current();
    }
  }, [recordActivity]);

  /**
   * Logs out the user and redirects to login page.
   */
  const logout = useCallback(async () => {
    setIsLoggingOut(true);

    try {
      broadcastLogout();

      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      await Session.signOut();

      try {
        localStorage.removeItem(IDLE_SESSION_CONFIG.STORAGE_KEY);
      } catch (e) {
        // Ignore
      }

      window.location.href = '/';
    } catch (error) {
      window.location.href = '/';
    }
  }, [broadcastLogout]);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  /**
   * Starts the countdown timer when warning is shown.
   */
  const startCountdown = useCallback((initialTimeMs?: number) => {
    const startTime = Date.now();
    const duration = initialTimeMs ?? IDLE_SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_MS;

    setTimeRemaining(duration);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);

      setTimeRemaining(remaining);

      if (remaining <= 0) {
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        logoutRef.current();
      }
    }, 1000);
  }, []);

  /**
   * Checks the idle status and shows warning if needed.
   */
  const checkIdleStatus = useCallback(async () => {
    const sessionExists = await Session.doesSessionExist();

    if (!sessionExists) {
      return;
    }

    const now = Date.now();
    const lastActivity = lastActivityRef.current;
    const idleTime = now - lastActivity;
    const timeUntilTimeout = IDLE_SESSION_CONFIG.IDLE_TIMEOUT_MS - idleTime;
    const warningThreshold = IDLE_SESSION_CONFIG.WARNING_BEFORE_TIMEOUT_MS;

    if (timeUntilTimeout <= 0) {
      await logout();
    } else if (timeUntilTimeout <= warningThreshold && !showWarning) {
      setShowWarning(true);
      setTimeRemaining(timeUntilTimeout);
      startCountdown(timeUntilTimeout);
      broadcastWarning(timeUntilTimeout);
    }
  }, [showWarning, logout, startCountdown, broadcastWarning]);

  /**
   * Handle activity received from other tabs.
   */
  useEffect(() => {
    const handleActivity = (timestamp: number) => {
      lastActivityRef.current = timestamp;
      if (showWarning) {
        setShowWarning(false);
        setIsLoggingOut(false);
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
      }
    };

    const handleLogout = () => {
      setIsLoggingOut(true);
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      window.location.href = '/';
    };

    const handleWarning = (receivedTimeRemaining: number) => {
      if (!showWarning) {
        setShowWarning(true);
        setTimeRemaining(receivedTimeRemaining);
        startCountdown(receivedTimeRemaining);
      }
    };

    onActivityReceived(handleActivity);
    onLogoutReceived(handleLogout);
    onWarningReceived(handleWarning);
  }, [showWarning, startCountdown, onActivityReceived, onLogoutReceived, onWarningReceived]);

  /**
   * Ref to hold the latest checkIdleStatus function.
   */
  const checkIdleStatusRef = useRef(checkIdleStatus);
  useEffect(() => {
    checkIdleStatusRef.current = checkIdleStatus;
  }, [checkIdleStatus]);

  /**
   * Initialize on mount.
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(IDLE_SESSION_CONFIG.STORAGE_KEY);
      if (stored) {
        const timestamp = parseInt(stored, 10);
        if (!isNaN(timestamp)) {
          lastActivityRef.current = timestamp;
        }
      }
    } catch (e) {
      // Ignore
    }

    checkIdleStatusRef.current();
    checkIntervalRef.current = setInterval(() => {
      checkIdleStatusRef.current();
    }, IDLE_SESSION_CONFIG.CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  /**
   * Handle visibility change (tab becomes visible/hidden).
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkIdleStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkIdleStatus]);

  /**
   * Listen for API activity events from axios interceptor.
   */
  useEffect(() => {
    const handleApiActivity = (event: CustomEvent<{ timestamp: number }>) => {
      recordActivity();
    };

    window.addEventListener('ltdan-api-activity', handleApiActivity as EventListener);

    return () => {
      window.removeEventListener('ltdan-api-activity', handleApiActivity as EventListener);
    };
  }, [recordActivity]);

  const contextValue: IdleDetectionContextValue = {
    showWarning,
    timeRemaining,
    extendSession,
    logout,
    recordActivity,
    isLoggingOut,
  };

  return (
    <IdleDetectionContext.Provider value={contextValue}>{children}</IdleDetectionContext.Provider>
  );
};
